import React, { useState } from 'react';
import { 
  AlertTriangle, Search, TrendingUp, DollarSign, 
  FileText, Shield, Activity, Download, Snowflake,
  Network, ExternalLink, ChevronRight
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Scatter, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import StatCard from '../../components/molecules/StatCard';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { downloadInvestigationReport, downloadNetworkGraphData, downloadClaimsReport } from '../../utils/pdfGenerator';

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip, Legend);

interface Provider {
  id: string;
  name: string;
  specialty: string;
  avgCost: number;
  patientCount: number;
  fwaScore: number;
  wasteScore: number;
  prescriptionScore: number;
  status: 'normal' | 'frozen' | 'at-risk';
}

interface Alert {
  id: string;
  type: 'critical' | 'warning';
  provider: string;
  issue: string;
  amountAtRisk: number;
  priority: 'urgent' | 'high' | 'medium';
  date: string;
}

const AlertDecisionDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'alerts' | 'analysis' | 'investigation'>('alerts');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  // Mock providers data
  const providers: Provider[] = [
    {
      id: '1',
      name: 'Dr. Ahmed Al-Mansouri',
      specialty: 'Cardiology',
      avgCost: 8500,
      patientCount: 45,
      fwaScore: 85,
      wasteScore: 120,
      prescriptionScore: 75,
      status: 'at-risk'
    },
    {
      id: '2',
      name: 'Dubai Medical Center',
      specialty: 'Multi-Specialty',
      avgCost: 12000,
      patientCount: 120,
      fwaScore: 45,
      wasteScore: 60,
      prescriptionScore: 35,
      status: 'normal'
    },
    {
      id: '3',
      name: 'Dr. Sarah Johnson',
      specialty: 'Orthopedics',
      avgCost: 15000,
      patientCount: 25,
      fwaScore: 92,
      wasteScore: 180,
      prescriptionScore: 90,
      status: 'frozen'
    },
    {
      id: '4',
      name: 'City Health Clinic',
      specialty: 'General Practice',
      avgCost: 6500,
      patientCount: 200,
      fwaScore: 35,
      wasteScore: 40,
      prescriptionScore: 30,
      status: 'normal'
    },
    {
      id: '5',
      name: 'Dr. Mohammed Al-Rashid',
      specialty: 'Neurology',
      avgCost: 11500,
      patientCount: 38,
      fwaScore: 78,
      wasteScore: 145,
      prescriptionScore: 68,
      status: 'at-risk'
    },
    {
      id: '6',
      name: 'Al-Ain Hospital',
      specialty: 'Multi-Specialty',
      avgCost: 9800,
      patientCount: 180,
      fwaScore: 42,
      wasteScore: 55,
      prescriptionScore: 40,
      status: 'normal'
    },
    {
      id: '7',
      name: 'Dr. Fatima Hassan',
      specialty: 'Pediatrics',
      avgCost: 5500,
      patientCount: 95,
      fwaScore: 28,
      wasteScore: 35,
      prescriptionScore: 25,
      status: 'normal'
    },
    {
      id: '8',
      name: 'Dr. John Martinez',
      specialty: 'Dermatology',
      avgCost: 7200,
      patientCount: 62,
      fwaScore: 55,
      wasteScore: 88,
      prescriptionScore: 52,
      status: 'normal'
    },
    {
      id: '9',
      name: 'Sharjah Specialized Clinic',
      specialty: 'Ophthalmology',
      avgCost: 8900,
      patientCount: 145,
      fwaScore: 48,
      wasteScore: 72,
      prescriptionScore: 45,
      status: 'normal'
    },
    {
      id: '10',
      name: 'Dr. Khalid Abdullah',
      specialty: 'Gastroenterology',
      avgCost: 13200,
      patientCount: 52,
      fwaScore: 88,
      wasteScore: 165,
      prescriptionScore: 82,
      status: 'at-risk'
    },
    {
      id: '11',
      name: 'Dr. Lisa Chen',
      specialty: 'Endocrinology',
      avgCost: 9500,
      patientCount: 71,
      fwaScore: 38,
      wasteScore: 48,
      prescriptionScore: 35,
      status: 'normal'
    },
    {
      id: '12',
      name: 'Abu Dhabi Family Clinic',
      specialty: 'General Practice',
      avgCost: 5800,
      patientCount: 220,
      fwaScore: 32,
      wasteScore: 38,
      prescriptionScore: 28,
      status: 'normal'
    },
    {
      id: '13',
      name: 'Dr. Omar Youssef',
      specialty: 'Urology',
      avgCost: 10800,
      patientCount: 48,
      fwaScore: 72,
      wasteScore: 128,
      prescriptionScore: 65,
      status: 'at-risk'
    },
    {
      id: '14',
      name: 'Dr. Emma Williams',
      specialty: 'Psychiatry',
      avgCost: 8200,
      patientCount: 58,
      fwaScore: 44,
      wasteScore: 62,
      prescriptionScore: 42,
      status: 'normal'
    },
    {
      id: '15',
      name: 'Ras Al Khaimah Medical Center',
      specialty: 'Multi-Specialty',
      avgCost: 11200,
      patientCount: 165,
      fwaScore: 50,
      wasteScore: 78,
      prescriptionScore: 48,
      status: 'normal'
    },
    {
      id: '16',
      name: 'Dr. Hassan Ibrahim',
      specialty: 'Pulmonology',
      avgCost: 14500,
      patientCount: 35,
      fwaScore: 95,
      wasteScore: 188,
      prescriptionScore: 92,
      status: 'frozen'
    },
    {
      id: '17',
      name: 'Dr. Maria Garcia',
      specialty: 'Obstetrics',
      avgCost: 9200,
      patientCount: 82,
      fwaScore: 40,
      wasteScore: 56,
      prescriptionScore: 38,
      status: 'normal'
    },
    {
      id: '18',
      name: 'Ajman Wellness Center',
      specialty: 'General Practice',
      avgCost: 6200,
      patientCount: 195,
      fwaScore: 36,
      wasteScore: 42,
      prescriptionScore: 32,
      status: 'normal'
    },
    {
      id: '19',
      name: 'Dr. Abdullah Al-Zaabi',
      specialty: 'Rheumatology',
      avgCost: 12800,
      patientCount: 42,
      fwaScore: 82,
      wasteScore: 152,
      prescriptionScore: 78,
      status: 'at-risk'
    },
    {
      id: '20',
      name: 'Dr. Rachel Thompson',
      specialty: 'Hematology',
      avgCost: 13500,
      patientCount: 38,
      fwaScore: 68,
      wasteScore: 115,
      prescriptionScore: 62,
      status: 'normal'
    },
    {
      id: '21',
      name: 'Dubai Sports Medicine Clinic',
      specialty: 'Orthopedics',
      avgCost: 11800,
      patientCount: 92,
      fwaScore: 52,
      wasteScore: 82,
      prescriptionScore: 50,
      status: 'normal'
    },
    {
      id: '22',
      name: 'Dr. Noor Al-Hashimi',
      specialty: 'Nephrology',
      avgCost: 14200,
      patientCount: 45,
      fwaScore: 86,
      wasteScore: 158,
      prescriptionScore: 80,
      status: 'at-risk'
    },
    {
      id: '23',
      name: 'Dr. David Anderson',
      specialty: 'Allergy & Immunology',
      avgCost: 7800,
      patientCount: 68,
      fwaScore: 42,
      wasteScore: 58,
      prescriptionScore: 40,
      status: 'normal'
    },
    {
      id: '24',
      name: 'Fujairah Medical Complex',
      specialty: 'Multi-Specialty',
      avgCost: 10500,
      patientCount: 155,
      fwaScore: 46,
      wasteScore: 68,
      prescriptionScore: 44,
      status: 'normal'
    },
    {
      id: '25',
      name: 'Dr. Layla Mohammed',
      specialty: 'Oncology',
      avgCost: 16500,
      patientCount: 28,
      fwaScore: 65,
      wasteScore: 105,
      prescriptionScore: 58,
      status: 'normal'
    },
    {
      id: '26',
      name: 'Dr. Robert Wilson',
      specialty: 'Infectious Disease',
      avgCost: 12500,
      patientCount: 48,
      fwaScore: 75,
      wasteScore: 135,
      prescriptionScore: 70,
      status: 'at-risk'
    },
    {
      id: '27',
      name: 'Al-Dhafra Clinic',
      specialty: 'General Practice',
      avgCost: 6800,
      patientCount: 210,
      fwaScore: 34,
      wasteScore: 45,
      prescriptionScore: 30,
      status: 'normal'
    },
    {
      id: '28',
      name: 'Dr. Amina Khalil',
      specialty: 'Plastic Surgery',
      avgCost: 18500,
      patientCount: 32,
      fwaScore: 58,
      wasteScore: 92,
      prescriptionScore: 55,
      status: 'normal'
    },
    {
      id: '29',
      name: 'Dr. Michael Brown',
      specialty: 'Vascular Surgery',
      avgCost: 17200,
      patientCount: 35,
      fwaScore: 90,
      wasteScore: 175,
      prescriptionScore: 88,
      status: 'frozen'
    },
    {
      id: '30',
      name: 'Emirates Heart Center',
      specialty: 'Cardiology',
      avgCost: 14800,
      patientCount: 105,
      fwaScore: 48,
      wasteScore: 75,
      prescriptionScore: 46,
      status: 'normal'
    },
    {
      id: '31',
      name: 'Dr. Salem Al-Mansoori',
      specialty: 'ENT',
      avgCost: 8800,
      patientCount: 78,
      fwaScore: 50,
      wasteScore: 80,
      prescriptionScore: 48,
      status: 'normal'
    },
    {
      id: '32',
      name: 'Dr. Jennifer Lee',
      specialty: 'Anesthesiology',
      avgCost: 9500,
      patientCount: 88,
      fwaScore: 44,
      wasteScore: 65,
      prescriptionScore: 42,
      status: 'normal'
    },
    {
      id: '33',
      name: 'Abu Dhabi Pain Clinic',
      specialty: 'Pain Management',
      avgCost: 11500,
      patientCount: 62,
      fwaScore: 70,
      wasteScore: 125,
      prescriptionScore: 68,
      status: 'at-risk'
    },
    {
      id: '34',
      name: 'Dr. Yasmin Ahmed',
      specialty: 'Pathology',
      avgCost: 7500,
      patientCount: 125,
      fwaScore: 38,
      wasteScore: 52,
      prescriptionScore: 36,
      status: 'normal'
    },
    {
      id: '35',
      name: 'Dr. Thomas Wright',
      specialty: 'Radiology',
      avgCost: 10200,
      patientCount: 145,
      fwaScore: 46,
      wasteScore: 70,
      prescriptionScore: 44,
      status: 'normal'
    },
    {
      id: '36',
      name: 'Sharjah Children Hospital',
      specialty: 'Pediatrics',
      avgCost: 6500,
      patientCount: 185,
      fwaScore: 30,
      wasteScore: 38,
      prescriptionScore: 28,
      status: 'normal'
    },
    {
      id: '37',
      name: 'Dr. Mariam Al-Suwaidi',
      specialty: 'Gynecology',
      avgCost: 9800,
      patientCount: 72,
      fwaScore: 54,
      wasteScore: 85,
      prescriptionScore: 52,
      status: 'normal'
    },
    {
      id: '38',
      name: 'Dr. Christopher Davis',
      specialty: 'Cardiothoracic Surgery',
      avgCost: 19500,
      patientCount: 22,
      fwaScore: 88,
      wasteScore: 168,
      prescriptionScore: 85,
      status: 'at-risk'
    },
    {
      id: '39',
      name: 'Dubai Eye Institute',
      specialty: 'Ophthalmology',
      avgCost: 11200,
      patientCount: 132,
      fwaScore: 42,
      wasteScore: 62,
      prescriptionScore: 40,
      status: 'normal'
    },
    {
      id: '40',
      name: 'Dr. Fatima Al-Blooshi',
      specialty: 'Geriatrics',
      avgCost: 8500,
      patientCount: 65,
      fwaScore: 48,
      wasteScore: 72,
      prescriptionScore: 46,
      status: 'normal'
    },
    {
      id: '41',
      name: 'Dr. James Miller',
      specialty: 'Neurosurgery',
      avgCost: 21500,
      patientCount: 18,
      fwaScore: 94,
      wasteScore: 185,
      prescriptionScore: 90,
      status: 'frozen'
    },
    {
      id: '42',
      name: 'Al-Ain Diabetes Center',
      specialty: 'Endocrinology',
      avgCost: 8800,
      patientCount: 98,
      fwaScore: 40,
      wasteScore: 58,
      prescriptionScore: 38,
      status: 'normal'
    },
    {
      id: '43',
      name: 'Dr. Huda Al-Mazrouei',
      specialty: 'Physical Medicine',
      avgCost: 7800,
      patientCount: 85,
      fwaScore: 46,
      wasteScore: 68,
      prescriptionScore: 44,
      status: 'normal'
    },
    {
      id: '44',
      name: 'Dr. Daniel Taylor',
      specialty: 'Hepatology',
      avgCost: 13800,
      patientCount: 42,
      fwaScore: 80,
      wasteScore: 148,
      prescriptionScore: 75,
      status: 'at-risk'
    },
    {
      id: '45',
      name: 'RAK Dental & Medical Center',
      specialty: 'Multi-Specialty',
      avgCost: 9500,
      patientCount: 175,
      fwaScore: 44,
      wasteScore: 65,
      prescriptionScore: 42,
      status: 'normal'
    },
    {
      id: '46',
      name: 'Dr. Sara Al-Ketbi',
      specialty: 'Dermatology',
      avgCost: 8200,
      patientCount: 88,
      fwaScore: 52,
      wasteScore: 78,
      prescriptionScore: 50,
      status: 'normal'
    },
    {
      id: '47',
      name: 'Dr. Andrew Robinson',
      specialty: 'Bariatric Surgery',
      avgCost: 16800,
      patientCount: 28,
      fwaScore: 66,
      wasteScore: 112,
      prescriptionScore: 62,
      status: 'normal'
    },
    {
      id: '48',
      name: 'Dubai Fertility Center',
      specialty: 'Reproductive Medicine',
      avgCost: 14500,
      patientCount: 55,
      fwaScore: 56,
      wasteScore: 90,
      prescriptionScore: 54,
      status: 'normal'
    },
    {
      id: '49',
      name: 'Dr. Noura Al-Shamsi',
      specialty: 'Intensive Care',
      avgCost: 12500,
      patientCount: 68,
      fwaScore: 74,
      wasteScore: 132,
      prescriptionScore: 70,
      status: 'at-risk'
    },
    {
      id: '50',
      name: 'Dr. Peter Anderson',
      specialty: 'Emergency Medicine',
      avgCost: 10800,
      patientCount: 155,
      fwaScore: 50,
      wasteScore: 75,
      prescriptionScore: 48,
      status: 'normal'
    }
  ];

  // Mock alerts
  const criticalAlerts: Alert[] = [
    {
      id: '1',
      type: 'critical',
      provider: 'Dr. Sarah Johnson',
      issue: 'Upcoding detected - 15 claims with inflated diagnosis codes',
      amountAtRisk: 87500,
      priority: 'urgent',
      date: dayjs().subtract(2, 'hours').format('YYYY-MM-DD HH:mm')
    },
    {
      id: '2',
      type: 'critical',
      provider: 'Dr. Ahmed Al-Mansouri',
      issue: 'Duplicate billing - Same procedure charged twice for 8 patients',
      amountAtRisk: 45200,
      priority: 'urgent',
      date: dayjs().subtract(5, 'hours').format('YYYY-MM-DD HH:mm')
    },
    {
      id: '3',
      type: 'warning',
      provider: 'Dubai Medical Center',
      issue: 'High brand medication rate - 85% vs industry 35%',
      amountAtRisk: 32000,
      priority: 'high',
      date: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm')
    }
  ];

  // Scatter plot data
  const scatterData = {
    datasets: [
      {
        label: 'Providers',
        data: providers.map(p => ({ x: p.avgCost, y: p.wasteScore })),
        backgroundColor: providers.map(p => 
          p.wasteScore > 150 ? 'rgba(3, 4, 94, 0.6)' :
          p.wasteScore > 100 ? 'rgba(0, 119, 182, 0.6)' :
          'rgba(35, 93, 255, 0.6)'
        ),
        borderColor: providers.map(p => 
          p.wasteScore > 150 ? 'rgba(3, 4, 94, 1)' :
          p.wasteScore > 100 ? 'rgba(0, 119, 182, 1)' :
          'rgba(35, 93, 255, 1)'
        ),
        borderWidth: 2,
        pointRadius: 10,
        pointHoverRadius: 12,
      }
    ]
  };

  const scatterOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: function(context: any) {
            const provider = providers[context.dataIndex];
            return [
              `${provider.name}`,
              `Avg Cost: AED ${provider.avgCost}`,
              `Waste Score: ${provider.wasteScore}`,
              `Patients: ${provider.patientCount}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Average Cost per Patient (AED)', font: { weight: 'bold' as const } },
        grid: { color: '#F3F4F6' }
      },
      y: {
        title: { display: true, text: 'Waste Score (0-200)', font: { weight: 'bold' as const } },
        grid: { color: '#F3F4F6' },
        min: 0,
        max: 200
      }
    }
  };

  // Waste Leaderboard Bar Chart
  const wasteLeaderboardData = {
    labels: providers.sort((a, b) => b.wasteScore - a.wasteScore).map(p => p.name),
    datasets: [
      {
        label: 'Waste Score',
        data: providers.sort((a, b) => b.wasteScore - a.wasteScore).map(p => p.wasteScore),
        backgroundColor: providers
          .sort((a, b) => b.wasteScore - a.wasteScore)
          .map(p => p.wasteScore > 150 ? '#03045E' : p.wasteScore > 100 ? '#0077B6' : '#235DFF'),
        borderRadius: 8,
      }
    ]
  };

  const barOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        cornerRadius: 8,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        max: 200
      },
      y: {
        grid: { display: false }
      }
    }
  };

  // Electronic Prescription Evaluation
  const prescriptionData = {
    labels: ['E-Prescription Rate', 'Generic Substitution', 'Formulary Compliance', 'Prior Authorization'],
    datasets: [
      {
        label: 'Score (%)',
        data: [87, 65, 78, 92],
        backgroundColor: ['#235DFF', '#0077B6', '#03045E', '#5C7A99'],
        borderRadius: 8,
      }
    ]
  };

  const prescriptionOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: false },
        max: 100,
        ticks: {
          callback: function(value: string | number) {
            return value + '%';
          }
        }
      },
      y: {
        grid: { display: false }
      }
    }
  };

  const handleFreezePayment = (provider: Provider) => {
    setSelectedProvider(provider);
    setShowFreezeModal(true);
  };

  const confirmFreeze = () => {
    if (selectedProvider) {
      toast.success(`Payment frozen for ${selectedProvider.name}`);
      setShowFreezeModal(false);
      setSelectedProvider(null);
    }
  };

  const handleLaunchInvestigation = (providerName?: string) => {
    downloadInvestigationReport(providerName);
    toast.success('Investigation report generated and PDF downloaded');
  };

  const handleViewDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setShowDetailsModal(true);
  };

  const handleDownloadNetworkGraph = () => {
    downloadNetworkGraphData();
    toast.success('Network graph data exported successfully');
  };

  const handleExportData = () => {
    downloadClaimsReport();
    toast.success('Claims data exported successfully');
  };

  const renderAlertsTab = () => (
    <div className="space-y-6">
      {/* Critical Alert Scorecards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#03045E] rounded-2xl shadow-lg p-8 text-white border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[#03045E]" />
            </div>
            <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-full">URGENT</span>
          </div>
          <h3 className="text-lg font-bold mb-2">Critical FWA Alerts</h3>
          <p className="text-5xl font-extrabold mb-2">
            {criticalAlerts.filter(a => a.type === 'critical').length}
          </p>
          <p className="text-sm opacity-90">Require immediate action</p>
        </div>

        <div className="bg-[#0077B6] rounded-2xl shadow-lg p-8 text-white border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#0077B6]" />
            </div>
            <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-full">HIGH</span>
          </div>
          <h3 className="text-lg font-bold mb-2">Warning Alerts</h3>
          <p className="text-5xl font-extrabold mb-2">
            {criticalAlerts.filter(a => a.type === 'warning').length}
          </p>
          <p className="text-sm opacity-90">Review within 24 hours</p>
        </div>

        <div className="bg-[#03045E] rounded-2xl shadow-lg p-8 text-white border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
              <DollarSign className="w-8 h-8 text-[#03045E]" />
            </div>
            <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-full">TOTAL</span>
          </div>
          <h3 className="text-lg font-bold mb-2">Amount at Risk</h3>
          <p className="text-4xl font-extrabold mb-2">
            AED {criticalAlerts.reduce((sum, a) => sum + a.amountAtRisk, 0).toLocaleString()}
          </p>
          <p className="text-sm opacity-90">Across all active alerts</p>
        </div>
      </div>

      {/* Detailed Alert List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Active Alerts - Decision Zone</h3>
        
        <div className="space-y-4">
          {criticalAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`border-2 rounded-xl p-6 ${
                alert.type === 'critical'
                  ? 'border-[#0077B6]/30 bg-[#0077B6]/5'
                  : 'border-[#235DFF]/30 bg-[#235DFF]/5'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                        alert.priority === 'urgent'
                          ? 'bg-[#03045E]/20 text-[#03045E]'
                          : 'bg-[#0077B6]/20 text-[#0077B6]'
                      }`}
                    >
                      {alert.priority.toUpperCase()}
                    </span>
                    <span className="font-bold text-gray-900 text-lg">{alert.provider}</span>
                  </div>
                  <p className="text-gray-700 font-medium mb-3">{alert.issue}</p>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span>
                      Amount at Risk:{' '}
                      <strong className="text-[#03045E]">AED {alert.amountAtRisk.toLocaleString()}</strong>
                    </span>
                    <span>Detected: {dayjs(alert.date).format('MMM DD, YYYY HH:mm')}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      const provider = providers.find(p => p.name === alert.provider);
                      if (provider) handleFreezePayment(provider);
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-[#03045E] text-white text-sm rounded-lg hover:bg-[#0077B6] transition-colors"
                  >
                    <Snowflake className="w-4 h-4" />
                    Freeze Payment
                  </button>
                  <button 
                    onClick={() => handleViewDetails(alert)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-[#03045E] text-sm border-2 border-[#03045E]/20 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Large Freeze Payment Button */}
      <div className="bg-[#0077B6] rounded-2xl shadow-lg p-8 text-center border border-gray-200">
        <div className="flex items-center justify-center mb-4">
          <Snowflake className="w-16 h-16 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Emergency Payment Freeze</h3>
        <p className="text-white/90 mb-6 max-w-2xl mx-auto">
          Immediately halt all pending payments to high-risk providers. This action is irreversible and
          will trigger mandatory investigation procedures.
        </p>
        <button
          onClick={() => {
            if (confirm('⚠️ This will freeze ALL payments for providers with critical alerts. Continue?')) {
              toast.success('Emergency freeze activated for all critical alert providers');
            }
          }}
          className="bg-white text-[#03045E] hover:bg-gray-100 border-2 border-white font-bold text-lg px-8 py-4 rounded-xl transition-colors"
        >
          ACTIVATE EMERGENCY FREEZE
        </button>
      </div>
    </div>
  );

  const renderAnalysisTab = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Avg Waste Score"
          value="95"
          icon={Activity}
          iconBgColor="bg-[#0077B6]/10"
          iconColor="text-[#0077B6]"
          subtitle="Industry avg: 60"
        />
        <StatCard
          title="Providers Above Threshold"
          value={providers.filter(p => p.wasteScore > 100).length}
          icon={AlertTriangle}
          iconBgColor="bg-[#03045E]/10"
          iconColor="text-[#03045E]"
          subtitle="Waste score > 100"
        />
        <StatCard
          title="E-Prescription Rate"
          value="87%"
          icon={FileText}
          iconBgColor="bg-[#235DFF]/10"
          iconColor="text-[#235DFF]"
          trend={{ value: 8, isPositive: true, label: 'vs last month' }}
        />
        <StatCard
          title="Generic Substitution"
          value="65%"
          icon={TrendingUp}
          iconBgColor="bg-[#5C7A99]/10"
          iconColor="text-[#5C7A99]"
          subtitle="Target: 75%"
        />
      </div>

      {/* Scatter Plot */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Cost vs Waste Score Analysis</h3>
            <p className="text-sm text-gray-600 mt-1">
              Identify providers with high costs and high waste indicators
            </p>
          </div>
          <Button variant="outline" icon={Download} onClick={handleExportData}>
            Export Data
          </Button>
        </div>

        <div className="h-96">
          <Scatter data={scatterData} options={scatterOptions} />
        </div>

        <div className="mt-4 flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">Low Waste (&lt; 100)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">Medium Waste (100-150)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="text-gray-600">High Waste (&gt; 150)</span>
          </div>
        </div>
      </div>

      {/* Waste Leaderboard */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Provider Waste Leaderboard</h3>
        <p className="text-sm text-gray-600 mb-6">
          Ranking by total waste score (higher = more wasteful practices)
        </p>

        <div className="h-80">
          <Bar data={wasteLeaderboardData} options={barOptions} />
        </div>
      </div>

      {/* Electronic Prescription Evaluation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Electronic Prescription Evaluation</h3>
        <p className="text-sm text-gray-600 mb-6">Network-wide compliance metrics</p>

        <div className="h-64">
          <Bar data={prescriptionData} options={prescriptionOptions} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
            <p className="text-sm font-semibold text-green-900 mb-1">E-Prescription</p>
            <p className="text-3xl font-bold text-green-700">87%</p>
          </div>
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
            <p className="text-sm font-semibold text-yellow-900 mb-1">Generic</p>
            <p className="text-3xl font-bold text-yellow-700">65%</p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-center">
            <p className="text-sm font-semibold text-blue-900 mb-1">Formulary</p>
            <p className="text-3xl font-bold text-blue-700">78%</p>
          </div>
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-center">
            <p className="text-sm font-semibold text-purple-900 mb-1">Prior Auth</p>
            <p className="text-3xl font-bold text-purple-700">92%</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvestigationTab = () => (
    <div className="space-y-6">
      {/* Correlation Network Graph Placeholder */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          Suspicious Correlation Map - Network Graph
        </h3>
        <div className="bg-gray-50 rounded-xl p-16 text-center border-2 border-dashed border-gray-300">
          <div className="w-24 h-24 bg-[#0077B6]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Network className="w-12 h-12 text-[#0077B6]" />
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-3">Interactive Network Visualization</h4>
          <p className="text-gray-600 max-w-3xl mx-auto mb-6">
            Shows connections between providers, patients, pharmacies, and diagnoses. Nodes are sized by
            transaction volume, with edges representing relationships. Red clusters indicate potential fraud
            rings, while orange highlights anomalous patterns.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button icon={Activity}>Launch Network Analyzer</Button>
            <Button variant="outline" icon={Download} onClick={handleDownloadNetworkGraph}>Export Graph Data</Button>
          </div>
        </div>
      </div>

      {/* Anomaly Search Tool */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Anomaly Search Tool</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Input
            placeholder="Provider name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>

        {/* Search Results */}
        <div className="space-y-4">
          {providers
            .filter(p => p.fwaScore > 60)
            .map((provider) => (
              <div key={provider.id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-gray-900 text-lg">{provider.name}</span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                          provider.fwaScore > 80
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        FWA Score: {provider.fwaScore}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{provider.specialty}</p>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span>Detected Anomalies: <strong>3</strong></span>
                      <span>Amount: <strong>AED {(provider.avgCost * 5).toLocaleString()}</strong></span>
                      <span>Status: <strong className="text-orange-600">Under Review</strong></span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" icon={ExternalLink}>
                    View Full Profile
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Launch Investigation Button */}
      <div className="bg-[#03045E] rounded-2xl shadow-lg p-8 text-center border border-gray-200">
        <div className="flex items-center justify-center mb-4">
          <FileText className="w-16 h-16 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Launch Comprehensive Investigation</h3>
        <p className="text-white/90 mb-6 max-w-2xl mx-auto">
          Generate a detailed investigation report with all evidence, network graphs, timeline analysis, and
          recommended enforcement actions. PDF will include legal documentation for regulatory submission.
        </p>
        <Button
          variant="outline"
          size="lg"
          icon={Download}
          onClick={() => handleLaunchInvestigation()}
          className="bg-white text-[#03045E] hover:bg-gray-50 border-2 border-white font-bold text-lg px-8 py-4"
        >
          GENERATE INVESTIGATION REPORT (PDF)
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Alert & Decision Dashboard</h1>
          <p className="text-lg text-gray-600">
            {dayjs().format('dddd, MMMM D, YYYY')} • Real-time fraud detection and enforcement
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 px-2">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('alerts')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'alerts'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alert and Decision Zone
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'analysis'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Provider Behavioral Analysis
              </div>
            </button>
            <button
              onClick={() => setActiveTab('investigation')}
              className={`px-6 py-4 font-semibold text-sm whitespace-nowrap ${
                activeTab === 'investigation'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Fraud Network Investigation
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'alerts' && renderAlertsTab()}
          {activeTab === 'analysis' && renderAnalysisTab()}
          {activeTab === 'investigation' && renderInvestigationTab()}
        </div>
      </div>

      {/* Alert Details Modal */}
      {showDetailsModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  selectedAlert.type === 'critical' ? 'bg-[#03045E]/10' : 'bg-[#0077B6]/10'
                }`}>
                  <AlertTriangle className={`w-6 h-6 ${
                    selectedAlert.type === 'critical' ? 'text-[#03045E]' : 'text-[#0077B6]'
                  }`} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Alert Details</h3>
                  <p className="text-sm text-gray-600">Alert ID: {selectedAlert.id}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Alert Summary */}
            <div className={`rounded-xl p-6 mb-6 ${
              selectedAlert.type === 'critical' 
                ? 'bg-[#03045E]/5 border-2 border-[#03045E]/20' 
                : 'bg-[#0077B6]/5 border-2 border-[#0077B6]/20'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                  selectedAlert.priority === 'urgent'
                    ? 'bg-[#03045E] text-white'
                    : 'bg-[#0077B6] text-white'
                }`}>
                  {selectedAlert.priority.toUpperCase()}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                  selectedAlert.type === 'critical'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {selectedAlert.type.toUpperCase()}
                </span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">{selectedAlert.provider}</h4>
              <p className="text-gray-700 text-lg mb-4">{selectedAlert.issue}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Amount at Risk</p>
                  <p className="text-2xl font-bold text-[#03045E]">AED {selectedAlert.amountAtRisk.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Detected</p>
                  <p className="text-lg font-semibold text-gray-900">{dayjs(selectedAlert.date).format('MMM DD, YYYY HH:mm')}</p>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0077B6]" />
                Provider Information
              </h4>
              {(() => {
                const provider = providers.find(p => p.name === selectedAlert.provider);
                if (!provider) return <p className="text-gray-600">Provider information not available</p>;
                return (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Specialty</p>
                      <p className="font-semibold text-gray-900">{provider.specialty}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">FWA Score</p>
                      <p className="font-semibold text-gray-900">{provider.fwaScore}/100</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Patient Count</p>
                      <p className="font-semibold text-gray-900">{provider.patientCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Avg Cost</p>
                      <p className="font-semibold text-gray-900">AED {provider.avgCost.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Waste Score</p>
                      <p className="font-semibold text-gray-900">{provider.wasteScore}/200</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        provider.status === 'frozen' ? 'bg-red-100 text-red-700' :
                        provider.status === 'at-risk' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {provider.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Investigation Details */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0077B6]" />
                Investigation Details
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p className="text-gray-700">
                    <strong>Detection Method:</strong> Automated AI-based pattern recognition
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p className="text-gray-700">
                    <strong>Claims Affected:</strong> {Math.floor(Math.random() * 20) + 5} claims flagged
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p className="text-gray-700">
                    <strong>Time Period:</strong> Last 30 days
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p className="text-gray-700">
                    <strong>Confidence Level:</strong> {selectedAlert.type === 'critical' ? '95%' : '82%'}
                  </p>
                </div>
              </div>
            </div>

            {/* Recommended Actions */}
            <div className="bg-[#0077B6]/5 rounded-xl p-6 mb-6 border border-[#0077B6]/20">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Recommended Actions</h4>
              <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p>Review all flagged claims manually</p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p>Contact provider for documentation verification</p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p>Consider freezing payments if fraud confirmed</p>
                </div>
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-[#0077B6] mt-0.5" />
                  <p>Generate investigation report for audit trail</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowDetailsModal(false)} 
                className="flex-1"
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  const provider = providers.find(p => p.name === selectedAlert.provider);
                  if (provider) {
                    setShowDetailsModal(false);
                    handleFreezePayment(provider);
                  }
                }}
                icon={Snowflake}
                className="flex-1 bg-[#03045E] hover:bg-[#0077B6]"
              >
                Freeze Payment
              </Button>
              <Button 
                onClick={() => {
                  if (selectedAlert) {
                    handleLaunchInvestigation(selectedAlert.provider);
                    toast.success('Investigation report generated and downloaded');
                    setShowDetailsModal(false);
                  }
                }}
                icon={Download}
                className="flex-1 bg-[#0077B6] hover:bg-[#03045E]"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Freeze Payment Modal */}
      {showFreezeModal && selectedProvider && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Snowflake className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Freeze Payment</h3>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-900 font-semibold mb-2">⚠️ WARNING: Critical Action</p>
              <p className="text-red-700 text-sm">
                You are about to freeze all future payments to:
              </p>
              <p className="text-red-900 font-bold text-lg mt-2">{selectedProvider.name}</p>
            </div>

            <div className="space-y-3 text-sm text-gray-700 mb-6">
              <div className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-gray-500 mt-0.5" />
                <p>All pending claims will be immediately frozen</p>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-gray-500 mt-0.5" />
                <p>Investigation report will be automatically generated</p>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-gray-500 mt-0.5" />
                <p>Action will be logged in audit trail with your user ID</p>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-gray-500 mt-0.5" />
                <p>Provider will be notified via email within 24 hours</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowFreezeModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmFreeze} icon={Snowflake} className="flex-1">
                Confirm Freeze
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default AlertDecisionDashboard;
